import * as React from 'react';
import * as classNames from 'classnames';
import * as I18n from 'i18n-js';
import {
  reverb_feed_FeedEntry,
  Listing,
  Article,
} from '../schemas/rql';

import { ITEM_WITH_SLUG_ROUTE } from '../components/shared/url_helpers';

import WatchBadgeWithRQL from '@reverbdotcom/commons/dist/components/watch_badge';
import ConditionDisplay from '@reverbdotcom/commons/dist/components/condition_display';
import PriceDisplay from '@reverbdotcom/commons/dist/components/price_display';
import RibbonView from '@reverbdotcom/commons/dist/components/ribbon_view';
import CoreLink from '@reverbdotcom/commons/dist/components/core_link';
import HideFeedItem from './hide_feed_item';

import { get } from 'lodash';
import { buildListingClickEvent } from '@reverbdotcom/commons/dist/elog/mparticle_tracker';
import { withUserContext, IUserContext } from '@reverbdotcom/commons';
import { WEB } from '@reverbdotcom/commons/dist/constants';

export interface IProps extends IUserContext {
  entry?: reverb_feed_FeedEntry;
  position?: number;
  showEstimatedMonthlyPayment?: boolean;
}

export interface IEntryWithImages {
  images: {
    source: string;
  }[];
}

const LISTING_TYPE = 'LISTING';
const ARTICLE_TYPE = 'ARTICLE';

function getLC(locale, lcs) {
  const code = locale.split('-')[0];
  return lcs.filter(lc => lc.locale === code)[0];
}

function shouldTrackInteraction(entry: reverb_feed_FeedEntry): boolean {
  return !!(entry && entry.type === LISTING_TYPE && entry.entry);
}

/**
 * This is just the start for RQL and the Feed. We will need to backport
 * support for the rest of the types in the
 * discovery-ui/src/components/discovery/feed/feed_item.tsx component.
 *
 * In doing so we will need to add additional type unions both in RQL
 * and in the ApolloClient
 */

export class RQLFeedItem extends React.Component<IProps, null> {
  static defaultProps: Partial<IProps> = {
    position: 0,
  };

  tryGetEntry<K>() {
    return get(this.props, 'entry.entry') as K;
  }

  isEmpty() {
    return !this.props.entry;
  }

  isType(type) {
    return get(this.props, 'entry.type') === type;
  }

  isSold() {
    if (!this.isType(LISTING_TYPE)) { return false; }

    const listing = this.tryGetEntry<Listing>();
    return listing.state !== 'live';
  }

  isWatchedListing() {
    return !this.isEmpty() && this.props.entry.metadata && this.props.entry.metadata.sourceType === 'WATCH';
  }

  renderOriginalPrice() {
    if (!this.hasDiscount()) {
      return null;
    }

    const listing = this.tryGetEntry<Listing>();

    return (
      <div className="feed-item__original-price">
        {listing.pricing.originalPrice.display}
      </div>
    );
  }

  renderPrice() {
    if (!this.isType(LISTING_TYPE)) { return null; }

    const listing = this.tryGetEntry<Listing>();

    return (
      <div className="feed-item__details">
        <div className="feed-item__price">
          <PriceDisplay
            amountCents={listing.pricing.buyerPrice.amountCents}
            currency={listing.pricing.buyerPrice.currency}
            display={listing.pricing.buyerPrice.display}
            auction={listing.listingType === 'AUCTION'}
          />
        </div>
        <div className="feed-item__condition">
          <ConditionDisplay
            slug={listing.condition.conditionSlug}
            displayName={listing.condition.displayName}
          />
        </div>
      </div>
    );
  }

  getClassNames() {
    return classNames(
      'feed-item',
      { 'feed-item--placeholder': this.isEmpty() },
      { 'feed-item--listing': this.isType(LISTING_TYPE) },
      { 'feed-item--watched': this.isWatchedListing() },
      { 'feed-item--discounted': this.hasDiscount() },
      { 'feed-item--article': this.isType(ARTICLE_TYPE) },
      { 'feed-item--sold': this.isSold() },
    );
  }

  getFeedImageStyle() {
    const entry = this.tryGetEntry<any>();

    if (!entry) { return null; }

    let image;
    if (this.isType(LISTING_TYPE) || this.isType(ARTICLE_TYPE)) {
      image = get((entry as IEntryWithImages).images[0], 'source');
    }

    return { backgroundImage: `url(${image})` };
  }

  getHref() {
    if (this.isEmpty()) { return '#'; }

    if (this.isType(LISTING_TYPE)) {
      const listing = this.tryGetEntry<Listing>();
      return ITEM_WITH_SLUG_ROUTE.expand({
        slug: listing.slug,
        id: listing.id,
      });
    }

    if (this.isType(ARTICLE_TYPE)) {
      const article = this.tryGetEntry<Article>();
      return article.webLink.href;
    }

    return '#';
  }

  hasDiscount() {
    if (!this.isType(LISTING_TYPE)) { return false; }

    return !!this.tryGetEntry<Listing>().pricing.originalPrice;
  }

  renderRibbon() {
    if (!this.isType(LISTING_TYPE)) { return null; }

    const listing = this.tryGetEntry<Listing>();

    return (
      <RibbonView
        auction={listing.listingType === 'AUCTION'}
        listingStateDescription={listing.stateDescription}
        listingStateSlug={listing.state}
        discountDisplay={get(listing, 'pricing.ribbon.display')}
      />
    );
  }

  renderArticleCategory() {
    if (!this.isType(ARTICLE_TYPE)) { return null; }

    const article = this.tryGetEntry<Article>();
    if (!article.articleCategories[0]) { return null; }

    return (
      <div className="feed-item__article-category">
        {article.articleCategories[0].name}
      </div>
    );
  }

  renderAffirmCallout(listing) {
    const estimatedMonthlyPayment = listing?.estimatedMonthlyPaymentPlan?.estimatedMonthlyPayment?.display;
    if (!this.props.showEstimatedMonthlyPayment || !estimatedMonthlyPayment) {
      return null;
    }

    return (
      <div className="grid-card__affirm-callout">
        {I18n.t('commons.estimatedMonthlyPaymentCallout', { displayPrice: estimatedMonthlyPayment })}
      </div>
    );
  }

  entryIsLiveListing(entry) {
    return entry && entry.type === 'LISTING' && !this.isSold();
  }

  clickEvent() {
    if (!shouldTrackInteraction(this.props.entry)) {
      return null;
    }

    return buildListingClickEvent({
      listing: this.props.entry.entry,
      position: this.props.position,
      displayStyle: null,
    });
  }

  render() {
    const { entry } = this.props;
    const content = getLC(I18n.locale, get(entry, 'localizedContents', []));

    if (get(entry, 'deleted', false)) {
      return null;
    }

    const { user } = this.props;
    const openNewTab = user && user.deviceName === WEB;

    return (
      <div className={this.getClassNames()}>
        <CoreLink
          to={this.getHref()}
          target={openNewTab ? '_blank' : undefined}
          clickEvent={this.clickEvent()}
        >
          <div className="feed-item__main">
            <div
              className="feed-item__image-area"
              style={this.getFeedImageStyle()}
            >
              {this.renderArticleCategory()}
            </div>
            <div className="feed-item__info">
              <div className="feed-item__title">
                {get(entry, 'entry.title')}
              </div>
            </div>
          </div>
          <div className="feed-item__footer">
            {this.renderOriginalPrice()}
            {this.renderPrice()}
            {this.entryIsLiveListing(entry) && this.renderAffirmCallout(entry.entry)}
            <div className="feed-item__source">
              <div className="feed-item__source__text">
                {entry && get(content, 'shortDescription')}
              </div>
              <div className="feed-item__source__hide">
                <HideFeedItem entry={entry} />
              </div>
            </div>
          </div>
          {this.renderRibbon()}
        </CoreLink>
        {this.entryIsLiveListing(entry) &&
          <div className="grid-card__watch">
            <WatchBadgeWithRQL listing={entry.entry} />
          </div>
        }
      </div>
    );
  }
}

export default withUserContext(RQLFeedItem);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/feed/rql_feed_item.tsx